import React, { useState, useEffect } from 'react';
import { notificationService } from '../services/notificationService';
import { pushService } from '../services/pushService';

const NotificationSettings = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [pushStatus, setPushStatus] = useState('unknown'); // unknown | unsupported | needs-install | off | on
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMessage, setPushMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      refreshPushStatus();
    }
  }, [isOpen]);

  const loadSettings = () => {
    const currentSettings = notificationService.getSettings();
    const permission = notificationService.getPermissionStatus();
    setSettings(currentSettings);
    setPermissionStatus(permission);
  };

  const refreshPushStatus = async () => {
    if (!pushService.isConfigured() || !pushService.isSupported()) {
      setPushStatus('unsupported');
      return;
    }
    if (pushService.needsHomeScreenInstall()) {
      setPushStatus('needs-install');
      return;
    }
    const subscribed = await pushService.isSubscribed();
    setPushStatus(subscribed ? 'on' : 'off');
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    setPushMessage('');
    try {
      const result = await pushService.subscribe();
      if (result.ok) {
        setPushStatus('on');
        setPushMessage('✅ Lock-screen notifications are ON for this device.');
      } else if (result.reason === 'permission-denied') {
        setPushMessage('⚠️ Permission was denied. If no prompt appeared, remove the app from your Home Screen, re-add it, and try again.');
      } else if (result.reason === 'needs-home-screen') {
        setPushStatus('needs-install');
      } else if (result.reason === 'hub-unreachable') {
        setPushMessage('⚠️ Could not reach the notification server. Check your connection and try again.');
      } else {
        setPushMessage('⚠️ Could not enable lock-screen notifications on this device.');
      }
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    setPushMessage('');
    try {
      await pushService.unsubscribe();
      setPushStatus('off');
      setPushMessage('Lock-screen notifications are off for this device.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setPushBusy(true);
    setPushMessage('');
    try {
      const result = await pushService.sendTestPush(8);
      if (result.ok) {
        setPushMessage('📲 Test sent! Lock your phone now — the notification arrives in about 8 seconds.');
      } else {
        setPushMessage('⚠️ Could not send the test. Check your connection and try again.');
      }
    } finally {
      setPushBusy(false);
    }
  };

  const handleToggleNotifications = async () => {
    setLoading(true);
    try {
      if (!settings.enabled) {
        // Enable notifications
        const success = await notificationService.enable();
        if (success) {
          const newSettings = { ...settings, enabled: true };
          setSettings(newSettings);
          setPermissionStatus('granted');
        }
      } else {
        // Disable notifications
        notificationService.disable();
        const newSettings = { ...settings, enabled: false };
        setSettings(newSettings);
        notificationService.saveSettings(newSettings);
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    notificationService.saveSettings(newSettings);
  };

  const testNotification = async () => {
    if (settings.enabled) {
      await notificationService.testNotification();
    } else {
      // Test even without full permissions for iOS users
      await notificationService.showLocalNotification('Test Notification', {
        body: 'Testing notifications with sound and vibration! 🎉',
        tag: 'test-notification',
        data: { isEmergency: false }
      });
    }
  };

  if (!isOpen || !settings) return null;

  const canEnableNotifications = notificationService.isNotificationSupported();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ color: 'var(--text-dark)', margin: 0 }}>
            🔔 Notification Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-light)'
            }}
          >
            ×
          </button>
        </div>

        {/* Lock-screen push: arrives with the app closed and the phone locked */}
        {pushStatus !== 'unsupported' && (
          <div style={{
            padding: '16px',
            backgroundColor: pushStatus === 'on' ? '#e8f5e8' : '#f0f8ff',
            borderRadius: '8px',
            marginBottom: '20px',
            border: pushStatus === 'on' ? '2px solid var(--muted-sage)' : '2px solid var(--light-blue)'
          }}>
            <h3 style={{ color: 'var(--text-dark)', margin: '0 0 4px 0' }}>
              📲 Lock-Screen Notifications
            </h3>
            <p style={{ color: 'var(--text-light)', fontSize: '14px', margin: '0 0 12px 0' }}>
              Push notifications that arrive even when the phone is locked and the app is closed.
            </p>

            {pushStatus === 'needs-install' && (
              <div style={{ fontSize: '14px', color: 'var(--text-dark)' }}>
                <strong>One-time setup needed on iPhone:</strong>
                <ol style={{ margin: '8px 0 0 20px', fontSize: '13px' }}>
                  <li>Open this site in Safari</li>
                  <li>Tap the Share button, then "Add to Home Screen"</li>
                  <li>Open the app from the new Home Screen icon</li>
                  <li>Return here and tap "Turn On"</li>
                </ol>
              </div>
            )}

            {(pushStatus === 'off' || pushStatus === 'on') && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: pushStatus === 'on' ? 'var(--muted-sage)' : 'var(--coral)'
                }}>
                  {pushStatus === 'on' ? '● On for this device' : '○ Off for this device'}
                </span>

                {pushStatus === 'off' ? (
                  <button
                    onClick={handleEnablePush}
                    disabled={pushBusy}
                    className="btn btn-primary"
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    {pushBusy ? 'Turning On...' : 'Turn On'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleTestPush}
                      disabled={pushBusy}
                      className="btn btn-success"
                      style={{ fontSize: '14px', padding: '8px 16px' }}
                    >
                      {pushBusy ? 'Sending...' : '🔒 Test on Locked Phone'}
                    </button>
                    <button
                      onClick={handleDisablePush}
                      disabled={pushBusy}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', padding: '8px 16px' }}
                    >
                      Turn Off
                    </button>
                  </>
                )}
              </div>
            )}

            {pushMessage && (
              <p style={{
                fontSize: '13px',
                color: 'var(--text-dark)',
                margin: '12px 0 0 0',
                padding: '8px',
                backgroundColor: 'white',
                borderRadius: '6px'
              }}>
                {pushMessage}
              </p>
            )}

            {pushStatus === 'on' && (
              <p style={{ fontSize: '12px', color: 'var(--text-light)', margin: '12px 0 0 0' }}>
                In silent mode the phone vibrates and lights up but plays no sound
                (Apple does not allow web apps to override the mute switch).
                An Apple Watch taps your wrist for every alert even in silent mode.
              </p>
            )}
          </div>
        )}

        {!canEnableNotifications && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fff3cd',
            borderRadius: '8px',
            marginBottom: '20px',
            color: '#856404'
          }}>
            ℹ️ Limited notification support detected. We'll use alternative alert methods including sound and visual cues.
          </div>
        )}

        {canEnableNotifications && (
          <>
            {/* Main Toggle */}
            <div style={{
              padding: '16px',
              backgroundColor: 'var(--warm-cream)',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <div>
                  <h3 style={{ color: 'var(--text-dark)', margin: '0 0 4px 0' }}>
                    In-App Alerts
                  </h3>
                  <p style={{ color: 'var(--text-light)', fontSize: '14px', margin: 0 }}>
                    Sound, banner, and buzz while the app is open on screen
                  </p>
                </div>
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '60px',
                  height: '34px'
                }}>
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={handleToggleNotifications}
                    disabled={loading}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: settings.enabled ? 'var(--primary-blue)' : '#ccc',
                    transition: '0.4s',
                    borderRadius: '34px',
                    '&:before': {
                      position: 'absolute',
                      content: '""',
                      height: '26px',
                      width: '26px',
                      left: settings.enabled ? '30px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%'
                    }
                  }}>
                    <div style={{
                      position: 'absolute',
                      content: '""',
                      height: '26px',
                      width: '26px',
                      left: settings.enabled ? '30px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%'
                    }} />
                  </span>
                </label>
              </div>

              {permissionStatus === 'denied' && (
                <div style={{
                  fontSize: '12px',
                  color: 'var(--coral)',
                  marginTop: '8px'
                }}>
                  ⚠️ Notifications blocked. Please enable in browser settings.
                </div>
              )}
            </div>

            {/* Detailed Settings */}
            {settings.enabled && permissionStatus === 'granted' && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: 'var(--text-dark)', marginBottom: '16px' }}>
                  Notification Types
                </h3>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '12px',
                    backgroundColor: 'var(--light-blue)',
                    borderRadius: '6px'
                  }}>
                    <input
                      type="checkbox"
                      checked={settings.newStudents}
                      onChange={(e) => handleSettingChange('newStudents', e.target.checked)}
                      style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                    />
                    <div>
                      <div style={{ fontWeight: 'bold', color: 'var(--text-dark)' }}>
                        👋 New Students
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-light)' }}>
                        Notify when a regular student joins the queue
                      </div>
                    </div>
                  </label>

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    padding: '12px',
                    backgroundColor: '#ffe6e6',
                    borderRadius: '6px'
                  }}>
                    <input
                      type="checkbox"
                      checked={settings.emergencies}
                      onChange={(e) => handleSettingChange('emergencies', e.target.checked)}
                      style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                    />
                    <div>
                      <div style={{ fontWeight: 'bold', color: 'var(--text-dark)' }}>
                        🚨 Emergency Patients
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--text-light)' }}>
                        Notify for emergency patients (higher priority)
                      </div>
                    </div>
                  </label>
                </div>

                {/* Test Notification */}
                <div style={{
                  marginTop: '20px',
                  textAlign: 'center'
                }}>
                  <button
                    onClick={testNotification}
                    className="btn btn-secondary"
                    style={{
                      fontSize: '14px',
                      padding: '8px 16px'
                    }}
                  >
                    🔔 Test Notification
                  </button>
                </div>
              </div>
            )}

            {/* Universal Test Button */}
            {!settings.enabled && (
              <div style={{
                marginTop: '20px',
                textAlign: 'center'
              }}>
                <button
                  onClick={testNotification}
                  className="btn btn-secondary"
                  style={{
                    fontSize: '14px',
                    padding: '8px 16px'
                  }}
                >
                  🔔 Test Alerts
                </button>
                <p style={{
                  fontSize: '12px',
                  color: 'var(--text-light)',
                  marginTop: '8px',
                  margin: '8px 0 0 0'
                }}>
                  Tests sound, vibration, and visual alerts
                </p>
              </div>
            )}

            {/* iOS-specific info */}
            {/iPad|iPhone|iPod/.test(navigator.userAgent) && (
              <div style={{
                padding: '12px',
                backgroundColor: '#f0f8ff',
                borderRadius: '6px',
                fontSize: '14px',
                color: 'var(--text-dark)',
                marginBottom: '12px'
              }}>
                📱 <strong>iPhone Users:</strong> For best results:
                <ul style={{ margin: '8px 0 0 20px', fontSize: '13px' }}>
                  <li>Open the app from its Home Screen icon (required for lock-screen alerts)</li>
                  <li>Turn on Lock-Screen Notifications above and send a test</li>
                  <li>Settings → Sounds &amp; Haptics → keep "Play Haptics in Silent Mode" on</li>
                  <li>In Settings → Notifications, set this app's banner style to "Persistent"</li>
                </ul>
              </div>
            )}
            
            {/* General Info */}
            <div style={{
              padding: '12px',
              backgroundColor: '#e8f4f8',
              borderRadius: '6px',
              fontSize: '14px',
              color: 'var(--text-dark)'
            }}>
              💡 <strong>Tip:</strong> Lock-Screen Notifications cover you when the phone is
              locked or you are in another app. In-App Alerts add sound and banners while
              the queue is on screen. Turn both on for full coverage.
            </div>
          </>
        )}

        <div style={{
          marginTop: '24px',
          textAlign: 'right'
        }}>
          <button
            onClick={onClose}
            className="btn btn-primary"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;